import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiX, FiMapPin, FiUser, FiPhone, FiMail, FiClock, FiUsers, FiFileText, FiExternalLink, FiPackage, FiMessageSquare, FiChevronDown } from 'react-icons/fi';
import axios from 'axios';
import { API_URL } from '../config';
import { getAuthToken } from '../services/api';

const STATUS_BADGE_COLORS = {
  'Pending': { bg: '#fff3cd', color: '#856404' },
  'Scheduled': { bg: '#cce5ff', color: '#004085' },
  'In Progress - Office Work': { bg: '#cffafe', color: '#155e75' },
  'In Progress - Initial Field Work': { bg: '#ede9fe', color: '#6b21a8' },
  'In Progress - Topo': { bg: '#e0e7ff', color: '#4338ca' },
  'In Progress - Additional Field Work Required': { bg: '#ddd6fe', color: '#5b21b6' },
  'In Progress - Drafting': { bg: '#ede9fe', color: '#6d28d9' },
  'Survey Complete': { bg: '#d1fae5', color: '#065f46' },
  'Needs Review': { bg: '#ffedd5', color: '#c2410c' },
  'Sent for Review': { bg: '#fef3c7', color: '#92400e' },
  'Approved': { bg: '#dcfce7', color: '#166534' },
  'Approved - Internal Review': { bg: '#dcfce7', color: '#166534' },
  'Approved - Planning & Zoning': { bg: '#bbf7d0', color: '#14532d' },
  'Submitted for Recording': { bg: '#ccfbf1', color: '#0f766e' },
  'Recorded': { bg: '#99f6e4', color: '#115e59' },
  'Final Mark Up / Stake Lines': { bg: '#fce7f3', color: '#be185d' },
  'Needs Invoice': { bg: '#fef3c7', color: '#92400e' },
  'Invoiced': { bg: '#fed7aa', color: '#c2410c' },
  'Paid': { bg: '#bbf7d0', color: '#166534' },
};

const PRIORITY_BADGE = {
  Urgent: { bg: '#fef2f2', color: '#dc2626', border: '#fecaca' },
  High: { bg: '#fff7ed', color: '#ea580c', border: '#fed7aa' },
  Normal: { bg: '#eff6ff', color: '#2563eb', border: '#bfdbfe' },
  Low: { bg: '#f9fafb', color: '#6b7280', border: '#e5e7eb' },
};

function formatTime(timeStr) {
  if (!timeStr) return '';
  const [h, m] = timeStr.split(':');
  const hour = parseInt(h, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const h12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${h12}:${m} ${ampm}`;
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
}

function formatTimeAgo(dateStr) {
  if (!dateStr) return '';
  let ts = dateStr;
  if (!ts.endsWith('Z') && !ts.includes('+')) ts = ts + 'Z';
  const date = new Date(ts);
  const diffMs = new Date() - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

export default function ScheduleJobPreviewModal({ jobId, onClose }) {
  const [loading, setLoading] = useState(true);
  const [job, setJob] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [comments, setComments] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [expandedWOs, setExpandedWOs] = useState(() => new Set());
  const [error, setError] = useState(null);

  const toggleWO = (id) => setExpandedWOs(prev => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const token = await getAuthToken();
        const config = { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} };
        const [jobRes, contactsRes, commentsRes, woRes] = await Promise.all([
          axios.get(`${API_URL}/jobs/${jobId}`, config),
          axios.get(`${API_URL}/jobs/${jobId}/contacts`, config).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/jobs/${jobId}/comments`, config).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/jobs/${jobId}/work-orders`, config).catch(() => ({ data: { work_orders: [] } })),
        ]);
        if (cancelled) return;
        setJob(jobRes.data);
        setContacts(Array.isArray(contactsRes.data) ? contactsRes.data : []);
        setComments(Array.isArray(commentsRes.data) ? commentsRes.data : []);
        setWorkOrders(woRes.data?.work_orders || []);
      } catch (e) {
        if (!cancelled) setError('Failed to load job details');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [jobId]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!jobId) return null;

  const statusStyle = (job?.status && STATUS_BADGE_COLORS[job.status]) || { bg: '#e5e7eb', color: '#374151' };
  const priorityStyle = PRIORITY_BADGE[job?.priority] || PRIORITY_BADGE.Normal;

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 99990
        }}
      />
      <div
        role="dialog"
        aria-modal="true"
        style={{
          position: 'fixed',
          top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 'min(640px, 92vw)',
          maxHeight: '88vh',
          backgroundColor: 'white',
          borderRadius: '14px',
          boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
          zIndex: 99995,
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        {/* Header */}
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px'
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#111827' }}>
                {job?.job_number || 'Loading...'}
              </h2>
              {job?.priority && (
                <span style={{
                  fontSize: '11px', fontWeight: 700, padding: '2px 8px',
                  borderRadius: '4px', textTransform: 'uppercase', letterSpacing: '0.5px',
                  backgroundColor: priorityStyle.bg, color: priorityStyle.color, border: `1px solid ${priorityStyle.border}`
                }}>{job.priority}</span>
              )}
              {job?.status && (
                <span style={{
                  fontSize: '12px', fontWeight: 600, padding: '3px 10px', borderRadius: '12px',
                  backgroundColor: statusStyle.bg, color: statusStyle.color
                }}>{job.status}</span>
              )}
            </div>
            {job?.client_name && (
              <div style={{ marginTop: '4px', color: '#6b7280', fontSize: '14px' }}>
                {job.client_name}
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              padding: '4px', color: '#6b7280', flexShrink: 0
            }}
          >
            <FiX size={22} />
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>Loading…</div>
          ) : error ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#dc2626' }}>{error}</div>
          ) : !job ? null : (
            <>
              {/* Schedule + Location */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '8px', marginBottom: '16px' }}>
                {job.scheduled_date && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#374151' }}>
                    <FiClock size={14} color="#6b7280" />
                    <strong>{formatDate(job.scheduled_date)}</strong>
                    {job.scheduled_time && <span style={{ color: '#6b7280' }}>at {formatTime(job.scheduled_time)}</span>}
                  </div>
                )}
                {job.property_address && (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '14px', color: '#374151' }}>
                    <FiMapPin size={14} color="#6b7280" style={{ marginTop: '3px', flexShrink: 0 }} />
                    <div>
                      <div>{job.property_address}</div>
                      {(job.location_city_state_zip || job.county) && (
                        <div style={{ color: '#6b7280', fontSize: '13px' }}>
                          {job.location_city_state_zip}
                          {job.location_city_state_zip && job.county ? ' • ' : ''}
                          {job.county ? `${job.county} County` : ''}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {Array.isArray(job.team_members) && job.team_members.length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#374151' }}>
                    <FiUsers size={14} color="#6b7280" />
                    {job.team_members.map(m => typeof m === 'string' ? m : m.name).join(', ')}
                  </div>
                )}
              </div>

              {/* Job-specific notes */}
              {job.job_specific_notes && (
                <Section icon={<FiFileText size={14} />} title="Notes">
                  <div style={{ whiteSpace: 'pre-wrap', fontSize: '13px', color: '#374151' }}>
                    {job.job_specific_notes}
                  </div>
                </Section>
              )}

              {/* Contacts */}
              {contacts.length > 0 && (
                <Section icon={<FiUser size={14} />} title={`Contacts (${contacts.length})`}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {contacts.map((c, idx) => (
                      <div key={c.id || `c-${idx}`} style={{
                        padding: '10px', backgroundColor: '#f9fafb', borderRadius: '8px',
                        border: '1px solid #f3f4f6', fontSize: '13px'
                      }}>
                        <div style={{ fontWeight: 600, color: '#111827' }}>
                          {c.name || c.contact_name || '—'}
                          {c.role && <span style={{ fontWeight: 400, color: '#6b7280' }}> · {c.role}</span>}
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginTop: '4px', color: '#374151' }}>
                          {c.phone && (
                            <a href={`tel:${c.phone}`} style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#2563eb', textDecoration: 'none' }}>
                              <FiPhone size={12} /> {c.phone}
                            </a>
                          )}
                          {c.email && (
                            <a href={`mailto:${c.email}`} style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#2563eb', textDecoration: 'none' }}>
                              <FiMail size={12} /> {c.email}
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </Section>
              )}

              {/* Work Orders */}
              {workOrders.length > 0 && (
                <Section icon={<FiPackage size={14} />} title={`Work Orders (${workOrders.length})`}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {workOrders.map(wo => {
                      const open = expandedWOs.has(wo.id);
                      return (
                        <div key={wo.id} style={{
                          backgroundColor: '#f9fafb', borderRadius: '6px',
                          border: '1px solid #f3f4f6', fontSize: '13px', overflow: 'hidden',
                        }}>
                          <div
                            onClick={() => toggleWO(wo.id)}
                            title="Click to see work-order details"
                            style={{ padding: '8px 10px', display: 'flex', justifyContent: 'space-between', gap: '8px', cursor: 'pointer', alignItems: 'center' }}
                          >
                            <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <FiChevronDown size={14} style={{ flexShrink: 0, color: '#9ca3af', transform: open ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.15s' }} />
                              <div style={{ minWidth: 0 }}>
                                <div style={{ fontWeight: 600, color: '#111827' }}>
                                  {wo.order_number}{wo.po_number ? ` · PO ${wo.po_number}` : ''}
                                </div>
                                <div style={{ color: '#6b7280', fontSize: '12px', overflow: open ? 'visible' : 'hidden', textOverflow: 'ellipsis', whiteSpace: open ? 'normal' : 'nowrap' }}>
                                  {wo.description}
                                </div>
                              </div>
                            </div>
                            <div style={{ fontSize: '11px', fontWeight: 600, color: '#6b7280', flexShrink: 0, textAlign: 'right' }}>
                              {wo.status}
                              {wo.scheduled_date && (
                                <div style={{ fontWeight: 400 }}>{formatDate(wo.scheduled_date)}</div>
                              )}
                            </div>
                          </div>
                          {open && (
                            <div style={{ padding: '0 10px 10px 32px', fontSize: '12px', color: '#374151', borderTop: '1px solid #f3f4f6', paddingTop: '8px' }}>
                              <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#9ca3af', marginBottom: '3px' }}>What's needed</div>
                              {wo.notes ? (
                                <div style={{ whiteSpace: 'pre-wrap' }}>{wo.notes}</div>
                              ) : (
                                <div style={{ color: '#9ca3af', fontStyle: 'italic' }}>No extra details on this work order — just "{wo.description}".</div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </Section>
              )}

              {/* Recent comments */}
              {comments.length > 0 && (
                <Section icon={<FiMessageSquare size={14} />} title="Recent Activity">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {comments.slice(0, 3).map(c => (
                      <div key={c.id} style={{
                        padding: '8px 10px', backgroundColor: '#f9fafb', borderRadius: '6px',
                        border: '1px solid #f3f4f6', fontSize: '13px'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                          <strong style={{ color: '#111827', fontSize: '12px' }}>{c.member_name || c.author_name || 'Someone'}</strong>
                          <span style={{ color: '#9ca3af', fontSize: '11px' }}>{formatTimeAgo(c.created_at)}</span>
                        </div>
                        <div style={{ color: '#374151', marginTop: '2px', whiteSpace: 'pre-wrap' }}>
                          {c.comment || c.content || ''}
                        </div>
                      </div>
                    ))}
                    {comments.length > 3 && (
                      <div style={{ fontSize: '12px', color: '#6b7280', textAlign: 'center' }}>
                        + {comments.length - 3} more
                      </div>
                    )}
                  </div>
                </Section>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 20px', borderTop: '1px solid #e5e7eb',
          display: 'flex', justifyContent: 'flex-end', gap: '8px'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 16px', fontSize: '14px', backgroundColor: 'white',
              border: '1px solid #d1d5db', borderRadius: '8px', cursor: 'pointer', color: '#374151'
            }}
          >
            Close
          </button>
          {jobId && (
            <Link
              to={`/jobs/${jobId}`}
              state={{ from: '/schedule', label: 'Schedule' }}
              onClick={onClose}
              style={{
                padding: '8px 16px', fontSize: '14px', backgroundColor: '#2563eb',
                border: '1px solid #2563eb', borderRadius: '8px', cursor: 'pointer', color: 'white',
                textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '6px'
              }}
            >
              Open Job <FiExternalLink size={14} />
            </Link>
          )}
        </div>
      </div>
    </>
  );
}

function Section({ icon, title, children }) {
  return (
    <div style={{ marginBottom: '14px' }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: '6px',
        fontSize: '12px', fontWeight: 700, color: '#6b7280',
        textTransform: 'uppercase', letterSpacing: '0.5px',
        marginBottom: '8px'
      }}>
        {icon} {title}
      </div>
      {children}
    </div>
  );
}
